import { useState, useEffect, useCallback } from 'react';
// import { doc, getDoc, setDoc } from 'firebase/firestore';
// import { db, localDb } from '../firebase/config';
import { useAuth } from '../context/AuthContext';

const DEFAULT_PREFERENCES = {
  necessary: true,
  functional: true,
  analytics: true,
  marketing: false
};

const STORAGE_KEY = 'cookieConsent';

/**
 * Custom hook for managing cookie consent preferences
 * - Currently uses localStorage only (for all users)
 * - Will sync with Central API for authenticated users
 * - Persists across login/logout/refresh
 * - Shows banner only when consent hasn't been given
 *
 * TODO: Integrate with Central API (Pattern A)
 * - Endpoint: PUT /user/cookie-consent
 * - Endpoint: GET /user/cookie-consent
 * - Per ARCHITECTURE.md: User preferences stored in paymentgateway-ab783
 * - See: /home/mainubu-home/Documents/REACT/FIREBASE/FireBase/ARCHITECTURE.md
 */
const useCookieConsent = () => {
  const { currentUser } = useAuth();
  const [preferences, setPreferences] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load preferences from localStorage
  const loadFromLocalStorage = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          ...DEFAULT_PREFERENCES,
          ...parsed.preferences,
          timestamp: parsed.timestamp
        };
      }
    } catch (error) {
      console.error('Error loading cookie preferences from localStorage:', error);
    }
    return null;
  }, []);

  // Save preferences to localStorage
  const saveToLocalStorage = useCallback((prefs) => {
    try {
      const data = {
        preferences: prefs,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving cookie preferences to localStorage:', error);
    }
  }, []);

  // TODO: Load preferences from Central API
  // Endpoint: GET /user/cookie-consent
  // Returns: { necessary, functional, analytics, marketing, timestamp }
  const loadFromFirebase = useCallback(async (userId) => {
    // COMMENTED OUT: Per Pattern A, user preferences stored centrally
    // TODO: Replace with Central API call
    // const response = await fetch(`${CENTRAL_API_URL}/user/cookie-consent`, {
    //   headers: { 'Authorization': `Bearer ${idToken}` }
    // });
    // return response.json();

    // try {
    //   const userDoc = await getDoc(doc(db, 'users', userId));
    //   if (userDoc.exists()) {
    //     const userData = userDoc.data();
    //     if (userData.cookiePreferences) {
    //       return {
    //         ...DEFAULT_PREFERENCES,
    //         ...userData.cookiePreferences,
    //         timestamp: userData.cookiePreferences.timestamp
    //       };
    //     }
    //   }
    // } catch (error) {
    //   console.error('Error loading cookie preferences from Firebase:', error);
    // }
    return null;
  }, []);

  // TODO: Save preferences to Central API
  // Endpoint: PUT /user/cookie-consent
  // Body: { necessary, functional, analytics, marketing }
  const saveToFirebase = useCallback(async (userId, prefs) => {
    // COMMENTED OUT: Per Pattern A, user preferences stored centrally
    // TODO: Replace with Central API call
    // const response = await fetch(`${CENTRAL_API_URL}/user/cookie-consent`, {
    //   method: 'PUT',
    //   headers: {
    //     'Authorization': `Bearer ${idToken}`,
    //     'Content-Type': 'application/json'
    //   },
    //   body: JSON.stringify(prefs)
    // });
    // return response.ok;

    // try {
    //   const data = {
    //     ...prefs,
    //     timestamp: new Date().toISOString()
    //   };
    //   await setDoc(
    //     doc(db, 'users', userId),
    //     {
    //       cookiePreferences: data,
    //       updatedAt: new Date().toISOString()
    //     },
    //     { merge: true }
    //   );
    //   return true;
    // } catch (error) {
    //   console.error('Error saving cookie preferences to Firebase:', error);
    //   return false;
    // }
    return false; // Temporarily disabled until Central API integration
  }, []);

  // Initialize preferences on mount and user change
  useEffect(() => {
    const initializePreferences = async () => {
      setLoading(true);

      try {
        if (currentUser) {
          // Authenticated user: check Firebase first, then localStorage
          let prefs = await loadFromFirebase(currentUser.uid);

          if (!prefs) {
            // No Firebase data, check localStorage
            prefs = loadFromLocalStorage();

            if (prefs) {
              // Migrate localStorage to Firebase
              await saveToFirebase(currentUser.uid, prefs);
            }
          }

          if (prefs) {
            setPreferences(prefs);
            setShowBanner(false);
          } else {
            setShowBanner(true);
          }
        } else {
          // Guest user: use localStorage only
          const prefs = loadFromLocalStorage();

          if (prefs) {
            setPreferences(prefs);
            setShowBanner(false);
          } else {
            setShowBanner(true);
          }
        }
      } catch (error) {
        console.error('Error initializing cookie preferences:', error);
        setShowBanner(true);
      } finally {
        setLoading(false);
      }
    };

    initializePreferences();
  }, [currentUser, loadFromLocalStorage, loadFromFirebase, saveToFirebase]);

  // Save preferences (to both localStorage and Firebase if authenticated)
  const savePreferences = useCallback(async (prefs) => {
    const normalizedPrefs = {
      necessary: true, // Always true
      functional: !!prefs.functional,
      analytics: !!prefs.analytics,
      marketing: !!prefs.marketing
    };

    // Always save to localStorage
    saveToLocalStorage(normalizedPrefs);

    // Save to Firebase if authenticated
    if (currentUser) {
      await saveToFirebase(currentUser.uid, normalizedPrefs);
    }

    setPreferences(normalizedPrefs);
    setShowBanner(false);

    return true;
  }, [currentUser, saveToLocalStorage, saveToFirebase]);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    return savePreferences({
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    });
  }, [savePreferences]);

  // Reject all non-essential cookies
  const rejectAll = useCallback(() => {
    return savePreferences({
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    });
  }, [savePreferences]);

  // Customize preferences
  const customize = useCallback((prefs) => {
    return savePreferences(prefs);
  }, [savePreferences]);

  // Reset preferences (for testing or user request)
  const resetPreferences = useCallback(async () => {
    localStorage.removeItem(STORAGE_KEY);

    // TODO: Delete preferences from Central API
    // Endpoint: DELETE /user/cookie-consent
    if (currentUser) {
      // COMMENTED OUT: Per Pattern A, user preferences stored centrally
      // TODO: Replace with Central API call
      // await fetch(`${CENTRAL_API_URL}/user/cookie-consent`, {
      //   method: 'DELETE',
      //   headers: { 'Authorization': `Bearer ${idToken}` }
      // });

      // try {
      //   await setDoc(
      //     doc(db, 'users', currentUser.uid),
      //     {
      //       cookiePreferences: null,
      //       updatedAt: new Date().toISOString()
      //     },
      //     { merge: true }
      //   );
      // } catch (error) {
      //   console.error('Error resetting cookie preferences in Firebase:', error);
      // }
    }

    setPreferences(null);
    setShowBanner(true);
  }, [currentUser]);

  // Check if a specific category is enabled
  const isEnabled = useCallback((category) => {
    if (!preferences) return false;
    return !!preferences[category];
  }, [preferences]);

  return {
    preferences,
    showBanner,
    loading,
    acceptAll,
    rejectAll,
    customize,
    resetPreferences,
    isEnabled
  };
};

export default useCookieConsent;
